<template>
  <div
    class="relative w-full flex-col items-center text-white bg-[#120F14] mt-[70px] pb-[80px]"
  >
    <!-- Scrollable Content Wrapper -->
    <div class="w-full h-full flex flex-col items-center justify-start">
      <!-- Header Container -->
      <div class="w-full max-w-[1400px] mx-auto pt-6 px-4 lg:px-8 xl:px-10">
        <!-- Banner -->
        <div class="relative w-full min-h-[180px] sm:min-h-[200px] overflow-hidden rounded-[16px] flex flex-col md:flex-row items-center justify-between p-6 sm:p-8 xl:p-10 gap-6 mb-6">
          <!-- Background Image -->
          <div class="absolute inset-0 bg-[#14181F] border border-white/5 rounded-[16px] pointer-events-none z-0"></div>
          <div class="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent z-0 pointer-events-none"></div>
          <div class="relative z-10 flex flex-col">
            <h1 class="text-4xl lg:text-5xl font-black text-white tracking-wide uppercase font-sans m-0">
              {{ $t('GuessTheBalance.Header') }}
            </h1>
            <p class="text-sm lg:text-base text-gray-300 mt-2 font-medium max-w-2xl">
              {{ $t('GuessTheBalance.Description') }}
            </p>
          </div>
        </div>

        <!-- Stat Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <!-- Total Sessions -->
          <div class="bg-[#14181F] rounded-xl p-6 flex flex-col justify-center border border-white/5">
            <div class="flex items-center gap-2 mb-2">
              <img src="@/assets/revamp/icons/hashtag.png" class="w-4 h-4 object-contain" />
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t("GuessTheBalance.TotalSessions") }}</span>
            </div>
            <div v-if="loading || animatedPoints === null" class="twist-spinner-small mt-2"></div>
            <div v-else class="text-3xl font-black text-white">{{ animatedGTBSessions }}</div>
          </div>

          <!-- Total Spent -->
          <div class="bg-[#14181F] rounded-xl p-6 flex flex-col justify-center border border-white/5">
            <div class="flex items-center gap-2 mb-2">
              <img src="@/assets/revamp/icons/money.png" class="w-4 h-4 object-contain" />
              <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t("GuessTheBalance.TotalSpent") }}</span>
            </div>
            <div v-if="loading || animatedTotalItems === null" class="twist-spinner-small mt-2"></div>
            <div v-else class="text-3xl font-black text-white">{{ animatedGTBSpent }}</div>
          </div>
        </div>
      </div>

      <!-- GTB Container -->
      <div class="flex flex-col justify-center items-center w-full max-w-[1400px] lg:pb-[50px] mx-auto">
        <!-- Show spinner if loading -->
        <div v-if="loading" class="flex justify-center items-center min-h-[300px] w-full">
          <div class="twist-spinner"></div>
        </div>

        <div v-else class="flex flex-col justify-center items-center w-full px-4 lg:px-8 xl:px-10">
          <div class="flex flex-col lg:flex-row justify-between items-center w-full mb-6">
            <h2 class="text-xl sm:text-2xl font-black uppercase font-sans mb-4 lg:mb-0">
              <span class="text-white">GTB</span> <span class="text-[#00F0FF]">{{ $t('GuessTheBalance.History') }}</span>
            </h2>

            <div class="flex flex-wrap items-center gap-3 justify-center lg:justify-end">
              <!-- Category Buttons -->
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="category in ['All', 'Current', 'Ended']"
                  :key="category"
                  @click="filterByCategory(category)"
                  class="px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold rounded-md transition-all border border-transparent"
                  :class="{
                    'bg-white/10 text-white border-white/20': selectedCategory === category,
                    'bg-[#0A0A0A] text-gray-400 hover:text-white': selectedCategory !== category,
                  }"
                >
                  {{ $t("Category.GuessTheBalance." + category) }}
                </button>
              </div>

              <!-- Refresh Button -->
              <button
                @click="refreshData"
                class="px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold rounded-md transition-all bg-[#14181F] text-white border border-white/5 hover:bg-white/5"
              >
                {{ $t("Button.Refresh") }}
              </button>
            </div>
          </div>

          <!-- GTB Table - Horizontally Scrollable -->
          <div class="w-full overflow-x-auto bg-[#14181F] rounded-xl border border-white/5 p-4 sm:p-6 mt-4">
            <div class="min-w-[1000px] flex flex-col">
              <!-- Table Header -->
              <div
                class="grid grid-cols-[1.5fr_1.5fr_2fr_1.5fr_1.5fr_1.5fr_2fr] gap-4 px-4 py-4 text-[10px] font-bold uppercase text-gray-500 border-b border-white/5"
              >
                <div class="text-left">{{ $t("GuessTheBalance.Table.status") }}</div>
                <div class="text-left">{{ $t("GuessTheBalance.Table.date") }}</div>
                <div class="text-right">
                  {{ $t("GuessTheBalance.Table.startingBalance") }}
                </div>
                <div class="text-right">{{ $t("GuessTheBalance.Table.biggestWin") }}</div>
                <div class="text-right">
                  {{ $t("GuessTheBalance.Table.endingBalance") }}
                </div>
                <div class="text-right">{{ $t("GuessTheBalance.Table.profitLoss") }}</div>
                <!-- <div class="text-right">{{ $t("GuessTheBalance.Table.winner") }}</div> -->
                <div class="text-right">{{ $t("GuessTheBalance.Table.action") }}</div>
              </div>

              <!-- Table Rows -->
              <div
                v-for="entry in filteredGTB"
                :key="entry.id"
                @click="$router.push(`/guess-the-balance/${entry.id}`)"
                class="grid grid-cols-[1.5fr_1.5fr_2fr_1.5fr_1.5fr_1.5fr_2fr] gap-4 px-4 py-4 items-center border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
              >
                <div class="flex justify-start items-center text-left">
                  <img
                    :src="
                      entry.status === 'active' || entry.status === 'started'
                        ? require('@/assets/gtb-active-status.png')
                        : entry.status === 'ongoing'
                        ? require('@/assets/gtb-ongoing-status.png')
                        : require('@/assets/gtb-done-status.png')
                    "
                    alt="Status"
                    class="h-[60%] w-[60%]"
                  />
                </div>
                <div class="text-left">{{ entry.date }}</div>
                <div class="text-right">
                  ${{ entry.startingBalance.toLocaleString() }}
                </div>
                <div class="text-right">
                  {{
                    entry.biggestWin > 0
                      ? `$${entry.biggestWin.toLocaleString()}`
                      : "----"
                  }}
                </div>
                <div class="text-right">
                  {{
                    entry.endingBalance > 0
                      ? `$${entry.endingBalance.toLocaleString()}`
                      : "----"
                  }}
                </div>
                <div
                  class="text-right"
                  :class="{
                    'text-green-500':
                      entry.status === 'done' &&
                      entry.endingBalance > entry.startingBalance,
                    'text-red-500':
                      entry.status === 'done' &&
                      entry.endingBalance < entry.startingBalance,
                    'text-white':
                      index === 0 && (entry.status !== 'done' || !entry.endingBalance),
                    'text-white':
                      index !== 0 && (entry.status !== 'done' || !entry.endingBalance),
                  }"
                >
                  {{
                    entry.status === "done" && entry.endingBalance
                      ? `${
                          entry.endingBalance < entry.startingBalance ? "-$" : "$"
                        }${Math.abs(entry.endingBalance - entry.startingBalance).toFixed(
                          2
                        )}`
                      : $t('GuessTheBalance.TBD')
                  }}
                </div>

                <!-- <div
                  class="text-right font-semibold"
                  :class="{
                    'text-green-500': entry.winner !== '----',
                    'text-white': index === 0 && entry.winner === '----',
                    'text-winner': index !== 0 && entry.winner === '----',
                  }"
                >
                  {{ truncateUsername(entry.winner) }}
                </div> -->

                <div class="text-right">
                  <button
                    v-if="entry.status === 'started'"
                    @click="$router.push(`/guess-the-balance/${entry.id}`)"
                    class="px-5 py-2 rounded-full bg-[#00F0FF] hover:bg-[#19a5cc] text-black text-xs font-bold whitespace-nowrap"
                  >
                    {{ $t('GuessTheBalance.JoinFor') }} {{ entry.requiredPoints || 0 }}
                    {{ entry.requiredPoints === 1 ? $t('GuessTheBalance.Point') : $t('GuessTheBalance.Points') }}
                  </button>

                  <button
                    v-else
                    @click="$router.push(`/guess-the-balance/${entry.id}`)"
                    class="px-6 py-2 rounded-full bg-[#00F0FF] hover:bg-[#19a5cc] text-black text-xs font-bold"
                  >
                    {{ $t('GuessTheBalance.View') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            class="flex justify-center w-full mr-14 items-center space-x-4 text-white font-bold mt-[40px] md:mt-[60px] ml-[60px]"
          >
            <!-- Background Container -->
            <div
              class="relative w-16 h-8 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden"
            >
              <!-- Animated Circle -->
              <div
                class="absolute top-1/2 left-0 transform -translate-y-1/2 w-4 h-4 rounded-full bg-white animate-scroll-dot"
              ></div>
            </div>

            <span>{{ $t('GuessTheBalance.ScrollToRight') }}</span>
          </div>

          <!-- Pagination -->
          <div
            class="mt-10 lg:mt-[60px] w-full py-[16px] lg:bg-transparent lg:backdrop-blur-sm"
          >
            <!-- Pagination Container with Gradient Border -->
            <div class="hidden lg:block w-full mt-6">
              <div class="p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-[10px]">
                <div class="bg-[#14181F] rounded-[9px] flex justify-between items-center w-full px-6 h-[70px]">
                  <p class="text-white font-bold text-lg">
                    {{ $t('Store.Showing') }} {{ itemStart }} - {{ itemEnd }} {{ $t('Store.Of') }} {{ totalItems }} {{ $t('Store.Items') }}
                  </p>
                  <div class="flex gap-2">
                    <!-- Prev Button -->
                    <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center w-10 h-10 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 hover:bg-[#1a1720] transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    
                    <!-- Page numbers -->
                    <span v-for="page in totalPages" :key="page">
                      <button @click="goToPage(page)" :class="['w-10 h-10 rounded-md font-bold transition-all border', currentPage === page ? 'bg-[#0D0B10] border-[#00F0FF] text-[#00F0FF]' : 'bg-[#0D0B10] border-transparent text-gray-500 hover:text-white hover:bg-[#1a1720]']">
                        {{ page }}
                      </button>
                    </span>

                    <!-- Next Button -->
                    <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center w-10 h-10 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 hover:bg-[#1a1720] transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Pagination Layout -->
            <div class="flex flex-col items-center mt-6 lg:hidden w-full">
              <div class="w-full p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-[10px]">
                <div class="bg-[#14181F] rounded-[9px] flex flex-col items-center w-full py-6 px-4">
                  <p class="text-white font-bold text-lg mb-4 text-center">
                    {{ $t('Store.Showing') }} {{ itemStart }} - {{ itemEnd }} {{ $t('Store.Of') }} {{ totalItems }} {{ $t('Store.Items') }}
                  </p>
                  <div class="flex flex-wrap justify-center gap-2">
                    <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center w-12 h-12 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
                    </button>

                    <span v-for="page in totalPages" :key="page">
                      <button @click="goToPage(page)" :class="['w-12 h-12 rounded-md font-bold transition-all border', currentPage === page ? 'bg-[#0D0B10] border-[#00F0FF] text-[#00F0FF]' : 'bg-[#0D0B10] border-transparent text-gray-500 hover:text-white']">
                        {{ page }}
                      </button>
                    </span>

                    <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center w-12 h-12 rounded-md bg-[#0D0B10] text-white disabled:opacity-50 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <RedirectModal
    :showModal="showRedirectModal"
    :user="$store.state.user"
    :isUserLoggedIn="isUserLoggedIn"
    @close="showRedirectModal = false"
  />
</template>

<script>
import axios from "axios";
import RedirectModal from "@/components/RedirectModal.vue";

export default {
  components: {
    RedirectModal,
  },
  data() {
    return {
      isReady: false,
      loading: false,

      // Guess The Balance stats
      totalGTBSessions: 0,
      totalGTBSpent: 0,
      animatedGTBSessions: 0,
      animatedGTBSpent: 0,

      // User points
      points: 0,
      animatedPoints: 0,

      // Leaderboard data
      gtbData: [],
      filteredGTB: [],

      // Filter & Sorting
      selectedCategory: "All",
      dropdownOpen: false,
      selectedSortOption: { label: this.$t("GuessTheBalance.Sort.all"), value: "all" },

      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalDocs: 0,
      limit: 10,

      showRedirectModal: false,
    };
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    itemStart() {
      return this.totalDocs === 0 ? 0 : (this.currentPage - 1) * this.limit + 1;
    },
    itemEnd() {
      return Math.min(this.currentPage * this.limit, this.totalDocs);
    },
    totalItems() {
      return this.totalDocs;
    },
  },
  methods: {
    async fetchGTBSessions() {
      if (this.loading) return;
      this.loading = true;

      // const token = localStorage.getItem("maherco-access-token");
      // if (!token) return (this.showRedirectModal = true);

      try {
        const baseUrl = process.env.VUE_APP_BASE_API;
        const params = { page: this.currentPage, limit: this.limit };

        const response = await axios.get(`${baseUrl}/api/guess-the-balance`, {
          params,
          headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
        });

        // console.log(response.data.gtbs);

        if (Array.isArray(response.data.gtbs)) {
          this.gtbData = [];
          // console.log(response.data.gtbs);
          this.processGTBData(response.data.gtbs);
          this.totalPages = response.data.totalPages || 1;
          this.totalDocs = response.data.totalDocs || 0;
        } else {
          console.warn("Unexpected API response format:", response.data);
        }

        if (response.data.gtbSummary) {
          this.totalGTBSessions = response.data.gtbSummary.totalBonusHunts || 0;
          this.totalGTBSpent = response.data.gtbSummary.totalCost || 0;
          this.animateValues();
        }
      } catch (error) {
        console.error("Error fetching GTB sessions:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchUserPoints() {
      const token = localStorage.getItem("maherco-access-token");
      if (!token) return;

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/users/me/points`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        this.points = data.points;
        this.animateValues();
      } catch (e) {
        console.error("Point fetch failed:", e);
      }
    },

    processGTBData(gtbs) {
      this.gtbData = gtbs.map((gtb) => {
        const bonus = gtb.bonusHuntId || {};
        const user = gtb.user || gtb.winner?.user || {};
        const winnerName = user.displayName || user.username || "----";

        return {
          id: gtb._id,
          date: new Date(bonus.createdAt || gtb.createdAt).toLocaleDateString("en-US"),
          createdAt: new Date(bonus.createdAt || gtb.createdAt),
          status: gtb.status || "N/A",
          startingBalance: bonus.infoStartCost || 0,
          biggestWin: bonus.highest_payout_value || 0,
          endingBalance: bonus.infoAmountWon || 0,
          winner: winnerName,
          prize: gtb.prize || 0,
          requiredPoints: gtb.requiredPoints || 0,
        };
      });

      this.filteredGTB = [...this.gtbData];
      this.filterByCategory(this.selectedCategory);
      this.animateValues();
    },

    filterByCategory(category) {
      this.selectedCategory = category;
      this.currentPage = 1;

      let filtered = [];

      if (category === "All") {
        filtered = [...this.gtbData];
      } else if (category === "Current") {
        filtered = this.gtbData.filter(
          (gtb) =>
            gtb.status === "active" ||
            gtb.status === "started" ||
            gtb.status === "ongoing"
        );
      } else if (category === "Ended") {
        filtered = this.gtbData.filter((gtb) => gtb.status === "done");
      }

      // Update docs & pagination after filtering
      this.totalDocs = filtered.length;
      this.totalPages = Math.ceil(this.totalDocs / this.limit);

      this.filteredGTB = filtered.slice(
        (this.currentPage - 1) * this.limit,
        this.currentPage * this.limit
      );
    },

    truncateUsername(username) {
      return username
        ? username.length > 10
          ? username.substring(0, 7) + "..."
          : username
        : "TBD";
    },

    animateValues() {
      this.animateNumber("animatedGTBSessions", this.totalGTBSessions);
      this.animateNumber("animatedGTBSpent", this.totalGTBSpent);
      this.animateNumber("animatedPoints", this.points);
    },
    animateNumber(variable, targetValue) {
      let startValue = parseInt(this[variable]) || 0;
      if (targetValue === startValue) return;

      const increment = Math.max(1, Math.ceil(targetValue / 30));
      const interval = setInterval(() => {
        startValue += increment;
        if (startValue >= targetValue) {
          this[variable] = targetValue.toLocaleString();
          clearInterval(interval);
        } else {
          this[variable] = startValue.toLocaleString();
        }
      }, 50);
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    selectCategory(category) {
      this.selectedCategory =
        category === "GTB Bonus Buy" || category === "GTB Spins Hunt" ? category : "All";
      this.dropdownOpen = false;
      this.fetchGTBSessions();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchGTBSessions();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchGTBSessions();
      }
    },
    refreshData() {
      this.fetchGTBSessions();
      this.fetchUserPoints();
    },
  },
  mounted() {
    this.fetchGTBSessions();
    this.fetchUserPoints();
  },
};
</script>

<style>
/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.loader {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid white;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.responsive-grid-xl {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

@media (min-width: 1280px) {
  .responsive-grid-xl {
    grid-template-columns: repeat(auto-fit, minmax(250px, 0fr));
  }
}

@keyframes scroll-dot {
  0% {
    transform: translate(20%, -50%);
    /* Start exactly at left */
    opacity: 0.8;
  }

  50% {
    transform: translate(270%, -50%);
    /* Move to far right */
    opacity: 1;
  }

  100% {
    transform: translate(20%, -50%);
    opacity: 0.8;
  }
}

.animate-scroll-dot {
  animation: scroll-dot 5s infinite ease-in-out;
}

@keyframes twist-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.twist-spinner {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 3px solid rgba(46, 187, 240, 0.15);
  border-top-color: #00F0FF;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-small {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid rgba(46, 187, 240, 0.15);
  border-top-color: #00F0FF;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-big {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(46, 187, 240, 0.15);
  border-top-color: #00F0FF;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

</style>
